import { useMemo } from 'react';
import { ExcelData, ColumnMappingData } from '../../pages/ExcelUploadPage';
import { isLegPresent, getMappedCellValue } from './legPresence';
import { hasIsolatedCountryCode, duplicateCountryCodeIssue } from './countryCodeInAddress';

export interface CellValidation {
  rowIndex: number;
  columnIndex: number;
  issue: string;
  severity: 'error' | 'warning';
}

export interface ColumnValidation {
  columnIndex: number;
  errors: number;
  warnings: number;
  issues: CellValidation[];
}

// --- pure per-rule validators (module-level) ---

const WEIGHT_FIELDS = new Set(['weight', 'weight_kg', 'cargo_weight']);
const VALID_MODES = ['air', 'sea', 'rail', 'ferry', 'iww', 'truck', 'road'];
const ROAD_MODES = new Set(['road', 'truck']);
const COUNTRY_FIELDS = new Set(['origin_country', 'destination_country', 'country']);
const POSTAL_FIELDS = new Set(['origin_postal_code', 'destination_postal_code', 'postal_code']);
const REQUIRED_FIELDS = new Set(['weight_kg', 'weight', 'cargo_weight', 'transport_mode']);
const ORIGIN_FIELDS = new Set(['origin_country', 'origin_city', 'origin_postal_code', 'origin_free_address']);
const DEST_FIELDS = new Set(['destination_country', 'destination_city', 'destination_postal_code', 'destination_free_address']);
const FREE_ADDRESS_FIELDS = new Set(['origin_free_address', 'destination_free_address']);

// Kept in sync with the dialog layer (DataValidationButton) — both suites assert these strings.
const LOAD_FACTOR_EMPTY_ISSUE = 'Load factor is mapped but empty — the default 80% will be used';
const nonRoadLoadFactorIssue = (legMode: string): string =>
  `Load factor only applies to Road/Truck legs — this leg's transport mode is '${legMode}'`;

const cell = (rowIndex: number, columnIndex: number, issue: string, severity: 'error' | 'warning'): CellValidation => ({
  rowIndex,
  columnIndex,
  issue,
  severity,
});

function validateWeight(value: string, r: number, c: number): CellValidation[] {
  const weight = Number.parseFloat(value.replace(',', '.'));
  if (value && (/[a-zA-Z]/.test(value) || Number.isNaN(weight))) {
    return [cell(r, c, 'Not a valid number', 'error')];
  }
  if (weight === 0) return [cell(r, c, 'Weight is zero', 'warning')];
  if (weight < 0) return [cell(r, c, 'Negative weight', 'error')];
  return [];
}

function validateCooling(value: string, r: number, c: number): CellValidation[] {
  if (value && !['true', 'false', 'yes', 'no'].includes(value.toLowerCase())) {
    return [cell(r, c, 'Must be true/false or yes/no', 'error')];
  }
  return [];
}

function validateTransportMode(value: string, r: number, c: number): CellValidation[] {
  if (value && !VALID_MODES.includes(value.toLowerCase())) {
    return [cell(r, c, `Invalid transport mode. Must be one of: ${VALID_MODES.join(', ')}`, 'error')];
  }
  return [];
}

function validateShipmentDate(value: string, r: number, c: number): CellValidation[] {
  if (!value) return [];
  if (!/[.\-/\s]/.test(value)) return [cell(r, c, 'Not a valid date', 'error')];
  const parts = value.split(/[.\-/]/);
  const manualValid = parts.length === 3 && !Number.isNaN(new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime());
  const nativeValid = !Number.isNaN(new Date(value).getTime());
  if (!manualValid && !nativeValid) return [cell(r, c, 'Not a valid date', 'error')];
  return [];
}

function validateCountry(value: string, r: number, c: number): CellValidation[] {
  if (value && !/^[A-Za-z]+$/.test(value)) return [cell(r, c, 'Country code must contain only letters', 'error')];
  if (value && value.length !== 2) return [cell(r, c, 'Country code must be 2 characters', 'error')];
  return [];
}

function validatePostal(value: string, r: number, c: number): CellValidation[] {
  const issues: CellValidation[] = [];
  if (value && value.length > 10) issues.push(cell(r, c, 'Postal code too long', 'warning'));
  if (value && !/^[A-Z0-9\s-]+$/i.test(value)) issues.push(cell(r, c, 'Invalid postal code characters', 'warning'));
  return issues;
}

function validateEmptyRequired(fieldName: string, r: number, c: number): CellValidation[] {
  if (REQUIRED_FIELDS.has(fieldName)) return [cell(r, c, 'Required field is empty', 'error')];
  if (ORIGIN_FIELDS.has(fieldName) || DEST_FIELDS.has(fieldName)) {
    return [cell(r, c, 'Location field is empty', 'warning')];
  }
  return [];
}

// Load factor: road/truck legs only, 0-100. A blank/unmapped leg mode defaults to
// Truck on the backend, so it counts as road here.
function validateLoadFactor(value: string, r: number, c: number, legMode: string): CellValidation[] {
  const isRoadLeg = !legMode || ROAD_MODES.has(legMode.toLowerCase());
  if (!value) {
    // Only worth mentioning on a leg that would actually use it.
    return isRoadLeg ? [cell(r, c, LOAD_FACTOR_EMPTY_ISSUE, 'warning')] : [];
  }
  const issues: CellValidation[] = [];
  if (!isRoadLeg) {
    issues.push(cell(r, c, nonRoadLoadFactorIssue(legMode), 'warning'));
  }
  const num = Number.parseFloat(value.replace(',', '.'));
  if (/[a-zA-Z]/.test(value) || Number.isNaN(num)) {
    issues.push(cell(r, c, 'Not a valid number', 'error'));
  } else if (num < 0 || num > 100) {
    issues.push(cell(r, c, 'Load factor must be between 0 and 100', 'error'));
  }
  return issues;
}

// The leg's own Country Code column already carries the country, so repeating it in the
// Free Address is redundant data rather than a hard error.
function validateFreeAddress(value: string, r: number, c: number, countryCode: string): CellValidation[] {
  if (!value || !countryCode || !hasIsolatedCountryCode(value, countryCode)) return [];
  return [cell(r, c, duplicateCountryCodeIssue(countryCode), 'warning')];
}

function validateCell(
  fieldName: string,
  value: string,
  r: number,
  c: number,
  legMode: string,
  countryCode: string
): CellValidation[] {
  const issues: CellValidation[] = [];
  if (WEIGHT_FIELDS.has(fieldName)) issues.push(...validateWeight(value, r, c));
  if (fieldName === 'cooling') issues.push(...validateCooling(value, r, c));
  if (fieldName === 'transport_mode') issues.push(...validateTransportMode(value, r, c));
  if (fieldName === 'shipment_date' || fieldName === 'shipmentDate') issues.push(...validateShipmentDate(value, r, c));
  if (COUNTRY_FIELDS.has(fieldName)) issues.push(...validateCountry(value, r, c));
  if (POSTAL_FIELDS.has(fieldName)) issues.push(...validatePostal(value, r, c));
  if (fieldName === 'load_factor') issues.push(...validateLoadFactor(value, r, c, legMode));
  if (FREE_ADDRESS_FIELDS.has(fieldName)) issues.push(...validateFreeAddress(value, r, c, countryCode));
  if (!value) issues.push(...validateEmptyRequired(fieldName, r, c));
  return issues;
}

export const useDataValidation = (
  excelData: ExcelData,
  mappings: ColumnMappingData,
  skipFirstRow: boolean = true
): { columnValidations: Map<number, ColumnValidation>; allIssues: CellValidation[] } => {
  return useMemo(() => {
    const columnValidations = new Map<number, ColumnValidation>();
    const allIssues: CellValidation[] = [];

    // Skip validation if no data
    if (!excelData?.rows?.length) {
      return { columnValidations, allIssues };
    }

    // Validate each mapped column
    Object.entries(mappings).forEach(([colKey, rawFieldId]) => {
      if (colKey.startsWith('MANUAL:')) return;
      const columnIndex = Number.parseInt(colKey, 10);
      if (Number.isNaN(columnIndex) || columnIndex < 0 || columnIndex >= excelData.headers.length) return;

      // For multi-select (||-delimited), use the first field ID for validation type detection
      const fieldId = rawFieldId.split('||')[0];

      const columnIssues: CellValidation[] = [];

      // Extract field name without leg suffix and get leg number
      const legMatch = /_leg(\d+)$/.exec(fieldId);
      const legNumber = legMatch ? Number.parseInt(legMatch[1]) : null;
      const fieldName = fieldId.replace(/_leg\d+$/, '');

      // Validate each row in this column (optionally skip first row if it's a header)
      excelData.rows.forEach((row, rowIndex) => {
        // Skip first row if user indicated it's a header
        if (skipFirstRow && rowIndex === 0) {
          return;
        }

        // Legs ≥ 2 are optional per row: skip fields of legs the backend would
        // not create (leg 1 is the mandatory base leg and is always validated).
        if (legNumber && legNumber >= 2 && !isLegPresent(row, mappings, legNumber)) {
          const trimmedValue = String(row[columnIndex] || '').trim();
          if (fieldName === 'transport_mode' && trimmedValue) {
            columnIssues.push(cell(rowIndex, columnIndex, `Transport mode set but leg ${legNumber} has no origin/destination`, 'error'));
          }
          return;
        }

        const trimmedValue = String(row[columnIndex] || '').trim();
        // Resolve the leg's transport mode only for load-factor's road-only rule.
        const legMode =
          fieldName === 'load_factor' && legNumber
            ? getMappedCellValue(row, mappings, `transport_mode_leg${legNumber}`)
            : '';
        // Resolve this side's Country Code column only for the free-address duplicate rule.
        const countryCode =
          FREE_ADDRESS_FIELDS.has(fieldName) && legNumber
            ? getMappedCellValue(row, mappings, `${fieldName.replace('_free_address', '_country')}_leg${legNumber}`)
            : '';
        columnIssues.push(...validateCell(fieldName, trimmedValue, rowIndex, columnIndex, legMode, countryCode));
      });

      // Store column validation summary
      if (columnIssues.length > 0) {
        const errors = columnIssues.filter((i) => i.severity === 'error').length;
        const warnings = columnIssues.filter((i) => i.severity === 'warning').length;

        columnValidations.set(columnIndex, {
          columnIndex,
          errors,
          warnings,
          issues: columnIssues,
        });

        allIssues.push(...columnIssues);
      }
    });

    return { columnValidations, allIssues };
  }, [excelData, mappings, skipFirstRow]);
};
